import React from "react";
import { useTranslation, Trans } from "next-i18next";
import parse from "html-react-parser";
import {
    PostgresIcon,
    MongoDBIcon,
    K8s, Linux, Windows
} from "../../components/v2/icons";
import AgentIntegration from "../../components/v2/agent-integration";
import Accordion from "../../components/v2/accordion/accordion";
import ProductResources from "../../components/v2/product/product-resource";
import ProductHeroSection from "../../components/v2/common/product-hero";
import ProductTour from "../../components/v2/product/product-tour";
import '../../styles/scss/_faq-section.module.scss';
import { Logs, APM, InfrastructureIcon } from "../../components/v2/icons-v2";
import ProductOverviewSection from "../../components/v2/product-overview-section";
import FeaturesMonitoringV2 from "../../components/v2/tabs/features-monitoring-v2";
import ClientTabsTestimonials from "../../components/v2/mw-carousel/testimonial-with-tabs";
import CallToActionDocsLink from "../../components/v2/blog/call-to-action-docs-link";

export default function DatabaseMonitoring() {
    const { t } = useTranslation("common");
    const featuresData = t('product.database-monitoring.features.detailedFeatures', { returnObjects: true }) as any[]
    const features = Array.isArray(featuresData) ? featuresData : []

    const faqsData = t('product.database-monitoring.faq.questions', { returnObjects: true }) as any[]
    const faqs = Array.isArray(faqsData) ? faqsData : []

    const resourceData = t('product.database-monitoring.resources', { returnObjects: true }) as any[]
    const resource = Array.isArray(resourceData) ? resourceData : []

    const productTourData = t('product.database-monitoring.productTour', { returnObjects: true }) as any

    const agentIntegrationData = t('product.database-monitoring.agentIntegration', { returnObjects: true }) as any

    const agentDeployData = t('product.shared.databaseDeploy', { returnObjects: true }) as any[]
    const agentDeployArray = Array.isArray(agentDeployData) ? agentDeployData : []

    const agentDeploy = agentDeployArray.map(agent => ({
        ...agent,
        icon: agent.key === 'postgresql' ? <PostgresIcon /> :
            agent.key === 'mongodb' ? <MongoDBIcon /> :
                <span className={"text-gradient"}>450+</span>
    }))

    const productOverviewData = {
        title: "Product overview",
        buttons: [
            {
                label: "View Docs",
                link: "https://docs.middleware.io/integrations/database_overview",
                className: "ahref-btn primary",
            },
            {
                label: "View Pricing",
                link: "/pricing/",
                className: "ahref-btn secondary",
            },
        ],
        description:
            "Middleware Database Monitoring provides real-time visibility into database performance across cloud, on-premises, container, and Kubernetes environments. Automatically detect slow queries, resource contention, and execution bottlenecks with out-of-the-box dashboards. Correlate database metrics with application and infrastructure performance in a single unified platform to eliminate data silos and accelerate root-cause analysis with full-stack observability.",
        worksWellWithTitle: "Works Well With",
        worksWellWith: [
            {
                title: "APM",
                link: "/product/apm/",
                icon: <APM />
            },
            {
                title: "Logs",
                link: "/product/log-monitoring/",
                icon: <Logs />
            },
            {
                title: "Infrastructure",
                link: "/product/infrastructure-monitoring/",
                icon: <InfrastructureIcon />
            },

        ],
    };

    return (
        <>
            <ProductHeroSection title={<h1>{t('product.database-monitoring.hero.title')} <span>{t('product.database-monitoring.hero.titleHighlight')}</span></h1>}
                description={t('product.database-monitoring.hero.description')}
                img={"/images/product/database-monitoring.png"} imgMobile={"/images/product/database-monitoring-mobile.webp"} imgAlt={t('product.database-monitoring.hero.imageAlt')} preloadImg hasActions={true} tips={t('product.hero.tips')} />

            <ProductOverviewSection productOverviewData={productOverviewData} />

            <FeaturesMonitoringV2 productFeatures={features} tourId={"database-tour"} description={''} />

            <CallToActionDocsLink
                title={'Start Optimizing Your Database Performance Today.'}
                docsLink="https://docs.middleware.io/integrations/database_overview"
            />

            <section className="testimonial-section">
                <div className="container">
                    <div className="section-tag testimonial">{t("testimonials.tag")} 👤</div>
                    <h2>Hear it from the best: Why <span className="text-gradient">top companies</span> trust Middleware</h2>

                    <ClientTabsTestimonials />
                </div>
            </section>

            <ProductTour tourSrc={"https://app.supademo.com/embed/clzgtkamw14ws9x77sfingh6a?embed_v=2"} title={productTourData.title} titleChanged={<h2>Take an <span className="text-gradient">Interactive Tour </span> for Database Monitoring Tool</h2>}  tourId={"database-tour"} description={productTourData.description} />

            <AgentIntegration
                agentDeploy={agentDeploy}
                title={
                    <h2>
                        <span>Integrate</span> in a snap
                    </h2>
                }
                description={"Achieve complete database and infrastructure visibility in minutes with a simple, single-command deployment. Get up and running quickly without disrupting existing workflows."}
                contactUsVariant="database-monitoring"
                nameClass={"database-integration-section"}
            />

            <ProductResources resources={resource} title={t('product.shared.resources.title')} />

            <section className={"faq-section"}>
                <div className={"container"}>
                    <h2>{t('product.database-monitoring.faq.title')}</h2>
                    <p>{t('product.database-monitoring.faq.subtitle')}</p>
                    <div className={"faq-item"}>
                        <Accordion faqs={faqs.map(faq => ({
                            ...faq,
                            answerPlain: faq.text,
                            text:
                                faq.textHtml != null && faq.textHtml !== ""
                                    ? parse(faq.textHtml)
                                    : <p>{faq.text}</p>
                        }))} showGroups={true} />
                    </div>
                </div>
            </section>
        </>
    )
}